import DatabaseManager from "../core/DatabaseManager";
import { createGuid } from "../../utils/cryptoHelper";

let instance = null;

class LOCATable extends DatabaseManager {
  constructor() {
    if (instance) {
      return instance;
    }
    super("gi-mobile", 1);
    this.storeName = "LOCA";
    instance = this;
  }

  // Create table structure
  async createDrillholeTable() {
    try {
      if (!this.db) {
        await this.initDatabase();
      }

      return this.executeTransaction(this.storeName, "readwrite", (store) => {
        return new Promise((resolve, reject) => {
          // IndexedDB doesn't need explicit table creation like SQLite
          // The store is created during database initialization in DatabaseManager
          resolve();
        });
      });
    } catch (error) {
      throw new Error(`Failed to create drillhole table: ${error.message}`);
    }
  }

  // Get all drillholes regardless of active status
  async getAllDrillholes(projectId) {
    try {
      if (!this.db) {
        await this.initDatabase();
      }

      return this.executeTransaction(this.storeName, "readonly", (store) => {
        return new Promise((resolve, reject) => {
          const index = store.index("project_id");
          const request = index.getAll(projectId);

          request.onsuccess = () => {
            const results = request.result.sort((a, b) => {
              return new Date(a.created_at) - new Date(b.created_at);
            });
            resolve(results);
          };

          request.onerror = () => {
            reject(request.error);
          };
        });
      });
    } catch (error) {
      throw new Error(`Failed to get all drillholes: ${error.message}`);
    }
  }

  // Get only active drillholes
  async getDrillholes(projectId) {
    try {
      if (!this.db) {
        await this.initDatabase();
      }

      return this.executeTransaction(this.storeName, "readonly", (store) => {
        return new Promise((resolve, reject) => {
          const index = store.index("project_id");
          const request = index.getAll(projectId);

          request.onsuccess = () => {
            const results = request.result
              .filter((drillhole) => drillhole.active === 1)
              .sort((a, b) => {
                return new Date(a.created_at) - new Date(b.created_at);
              });
            resolve(results);
          };

          request.onerror = () => {
            reject(request.error);
          };
        });
      });
    } catch (error) {
      throw new Error(`Failed to get active drillholes: ${error.message}`);
    }
  }

  // Add new drillhole
  async addDrillhole(drillhole, projectId, isFromCloud = false) {
    console.log("---addDrillhole---", drillhole);
    try {
      if (!this.db) {
        await this.initDatabase();
      }
      console.log("---addDrillhole---", drillhole);
      const id = drillhole.id || createGuid();
      const data = {
        id: id,
        LOCA_ID: drillhole.LOCA_ID,
        project_id: projectId,
        LOCA_TYPE: Array.isArray(drillhole.LOCA_TYPE)
          ? drillhole.LOCA_TYPE.join("+")
          : drillhole.LOCA_TYPE,
        LOCA_AS_NATE: drillhole.LOCA_AS_NATE,
        LOCA_AS_NATN: drillhole.LOCA_AS_NATN,
        LOCA_NATE: drillhole.LOCA_NATE,
        LOCA_NATN: drillhole.LOCA_NATN,
        LOCA_GREF: drillhole.LOCA_GREF,
        LOCA_AS_GL: drillhole.LOCA_AS_GL,
        LOCA_GL: drillhole.LOCA_GL,
        LOCA_REM: drillhole.LOCA_REM,
        drilling_type: drillhole.drilling_type,
        LOCA_STAR: drillhole.LOCA_STAR,
        LOCA_ENDD: drillhole.LOCA_ENDD,
        LOCA_STAT: drillhole.LOCA_STAT,
        LOCA_FDEP: drillhole.LOCA_FDEP,
        LOCA_PURP: drillhole.LOCA_PURP,
        LOCA_TERM: drillhole.LOCA_TERM,
        LOCA_LETT: drillhole.LOCA_LETT,
        LOCA_LOCX: drillhole.LOCA_LOCX,
        LOCA_LOCY: drillhole.LOCA_LOCY,
        LOCA_LOCZ: drillhole.LOCA_LOCZ,
        LOCA_LREF: drillhole.LOCA_LREF,
        LOCA_DATM: drillhole.LOCA_DATM,
        LOCA_ETRV: drillhole.LOCA_ETRV,
        LOCA_NTRV: drillhole.LOCA_NTRV,
        LOCA_LTRV: drillhole.LOCA_LTRV,
        LOCA_XTRL: drillhole.LOCA_XTRL,
        LOCA_YTRL: drillhole.LOCA_YTRL,
        LOCA_ZTRL: drillhole.LOCA_ZTRL,
        LOCA_LAT: drillhole.LOCA_LAT,
        LOCA_LON: drillhole.LOCA_LON,
        LOCA_ELAT: drillhole.LOCA_ELAT,
        LOCA_ELON: drillhole.LOCA_ELON,
        LOCA_LLZ: drillhole.LOCA_LLZ,
        LOCA_LOCM: drillhole.LOCA_LOCM,
        LOCA_LOCA: drillhole.LOCA_LOCA,
        LOCA_CLST: drillhole.LOCA_CLST,
        LOCA_ALID: drillhole.LOCA_ALID,
        LOCA_OFFS: drillhole.LOCA_OFFS,
        LOCA_CNGE: drillhole.LOCA_CNGE,
        LOCA_TRAN: drillhole.LOCA_TRAN,
        FILE_FSET: drillhole.FILE_FSET,
        LOCA_CKBY: drillhole.LOCA_CKBY,
        LOCA_CKDT: drillhole.LOCA_CKDT,
        LOCA_NATD: drillhole.LOCA_NATD,
        LOCA_ORID: drillhole.LOCA_ORID,
        LOCA_ORJO: drillhole.LOCA_ORJO,
        LOCA_ORCO: drillhole.LOCA_ORCO,
        created_at: isFromCloud
          ? drillhole.created_at
          : new Date().toISOString(),
        modified_at: isFromCloud
          ? drillhole.modified_at
          : new Date().toISOString(),
        active: 1,
      };

      return this.executeTransaction(this.storeName, "readwrite", (store) => {
        return new Promise((resolve, reject) => {
          const request = store.add(data);

          request.onsuccess = () => {
            resolve({
              ...drillhole,
              id: id,
              project_id: projectId,
            });
          };

          request.onerror = () => {
            reject(request.error);
          };
        });
      });
    } catch (error) {
      throw new Error(`Failed to add drillhole: ${error.message}`);
    }
  }

  // Update existing drillhole
  async updateDrillhole(drillhole, projectId, isFromCloud = false) {
    console.log("---updateDrillhole---", drillhole);
    try {
      if (!this.db) {
        await this.initDatabase();
      }

      const data = {
        ...drillhole,
        LOCA_TYPE: Array.isArray(drillhole.LOCA_TYPE)
          ? drillhole.LOCA_TYPE.join("+")
          : drillhole.LOCA_TYPE,
        project_id: projectId,
        modified_at: isFromCloud
          ? drillhole.modified_at
          : new Date().toISOString(),
      };

      return this.executeTransaction(this.storeName, "readwrite", (store) => {
        return new Promise((resolve, reject) => {
          const request = store.put(data);

          request.onsuccess = () => {
            resolve(drillhole);
          };

          request.onerror = () => {
            reject(request.error);
          };
        });
      });
    } catch (error) {
      throw new Error(`Failed to update drillhole: ${error.message}`);
    }
  }

  // Soft delete drillhole
  async deleteDrillhole(drillhole, isFromCloud = false) {
    try {
      if (!this.db) {
        await this.initDatabase();
      }

      return this.executeTransaction(this.storeName, "readwrite", (store) => {
        return new Promise((resolve, reject) => {
          const getRequest = store.get(drillhole.id);

          getRequest.onsuccess = () => {
            const data = getRequest.result;
            if (data) {
              data.active = 0;
              data.modified_at = isFromCloud
                ? drillhole.modified_at
                : new Date().toISOString();

              const updateRequest = store.put(data);

              updateRequest.onsuccess = () => {
                resolve(drillhole);
              };

              updateRequest.onerror = () => {
                reject(updateRequest.error);
              };
            } else {
              reject(new Error("Drillhole not found"));
            }
          };

          getRequest.onerror = () => {
            reject(getRequest.error);
          };
        });
      });
    } catch (error) {
      throw new Error(`Failed to delete drillhole: ${error.message}`);
    }
  }

  // Initialize new drillhole object
  initDrillhole() {
    return {
      id: "",
      LOCA_ID: "",
      project_id: "",
      LOCA_TYPE: [],
      LOCA_AS_NATE: 0,
      LOCA_AS_NATN: 0,
      LOCA_NATE: 0,
      LOCA_NATN: 0,
      LOCA_GREF: "",
      LOCA_AS_GL: 0,
      LOCA_GL: 0,
      LOCA_REM: "",
      drilling_type: "",
      LOCA_STAR: new Date(),
      LOCA_ENDD: new Date(),
      LOCA_STAT: "",
      LOCA_FDEP: 0,
      LOCA_PURP: "",
      LOCA_TERM: "",
      LOCA_LETT: "",
      LOCA_LOCX: 0,
      LOCA_LOCY: 0,
      LOCA_LOCZ: 0,
      LOCA_LREF: "",
      LOCA_DATM: "",
      LOCA_ETRV: 0,
      LOCA_NTRV: 0,
      LOCA_LTRV: 0,
      LOCA_XTRL: 0,
      LOCA_YTRL: 0,
      LOCA_ZTRL: 0,
      LOCA_LAT: 0,
      LOCA_LON: 0,
      LOCA_ELAT: 0,
      LOCA_ELON: 0,
      LOCA_LLZ: "",
      LOCA_LOCM: "",
      LOCA_LOCA: "",
      LOCA_CLST: "",
      LOCA_ALID: "",
      LOCA_OFFS: 0,
      LOCA_CNGE: "",
      LOCA_TRAN: "",
      FILE_FSET: "",
      LOCA_CKBY: "",
      LOCA_CKDT: "",
      LOCA_NATD: "",
      LOCA_ORID: "",
      LOCA_ORJO: "",
      LOCA_ORCO: "",
    };
  }
}

export const locaTable = new LOCATable();
